import { useEffect, useRef, useState } from "react";
import { loadedSiteVersion, latestSiteVersion, claimAutomaticRefresh, recordManualRefresh } from "../lib/site-release";
import { pageUpdateBlocked, UPDATE_SAFETY_EVENT } from "../lib/page-update-safety";

export function useSiteRelease() {
  const [release, setRelease] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [dismissed, setDismissed] = useState(null);
  const dismissedRef = useRef(null);

  useEffect(() => {
    let disposed = false, active = null, lastCheck = 0, refreshTimer = null, interactionVersion = 0;
    const safety = () => { if (!disposed) setBlocked(pageUpdateBlocked()); };
    const check = async (automatic = false, force = false) => {
      if (disposed || active || document.visibilityState !== "visible" || navigator.onLine === false) return;
      const current = loadedSiteVersion();
      if (!current || (!force && Date.now() - lastCheck < 15000)) return;
      lastCheck = Date.now();
      const interactionAtCheck = interactionVersion;
      const controller = new AbortController(); active = controller;
      const timeout = window.setTimeout(() => controller.abort(), 8000);
      try {
        const next = await latestSiteVersion(window.location.origin, controller.signal);
        if (disposed || !next) return;
        if (next === current) { window.clearTimeout(refreshTimer); setRelease(null); return; }
        setRelease(next); safety();
        if (automatic && interactionVersion === interactionAtCheck && dismissedRef.current !== next && !pageUpdateBlocked()) {
          // Recheck after React effects, browser autofill and any immediately arriving user input.
          window.clearTimeout(refreshTimer);
          refreshTimer = window.setTimeout(() => {
            if (!disposed && interactionVersion === interactionAtCheck && document.visibilityState === "visible" && navigator.onLine !== false &&
                !pageUpdateBlocked() && claimAutomaticRefresh(next)) window.location.reload();
          }, 1200);
        }
      } catch { /* Offline, invalid manifests or unavailable checks never interrupt a working page. */ }
      finally { window.clearTimeout(timeout); if (active === controller) active = null; }
    };
    const onResume = () => { safety(); if (document.visibilityState === "visible") check(true); };
    const onPageShow = event => { if (event.persisted) { safety(); check(true, true); } };
    const onInteraction = () => {
      interactionVersion += 1;
      // A visitor interacting now should see the notice, not an automatic refresh mid-action.
      window.clearTimeout(refreshTimer); refreshTimer = null;
      safety();
    };
    window.addEventListener("focus", onResume);
    window.addEventListener("online", onResume);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener(UPDATE_SAFETY_EVENT, safety);
    document.addEventListener("visibilitychange", onResume);
    document.addEventListener("input", onInteraction, true);
    document.addEventListener("change", onInteraction, true);
    document.addEventListener("pointerdown", onInteraction, true);
    document.addEventListener("keydown", onInteraction, true);
    const poll = window.setInterval(() => { safety(); check(false); }, 60000);
    safety(); check(true, true);
    return () => {
      disposed = true; active?.abort(); window.clearInterval(poll); window.clearTimeout(refreshTimer);
      window.removeEventListener("focus", onResume); window.removeEventListener("online", onResume);
      window.removeEventListener("pageshow", onPageShow); window.removeEventListener(UPDATE_SAFETY_EVENT, safety);
      document.removeEventListener("visibilitychange", onResume);
      document.removeEventListener("input", onInteraction, true); document.removeEventListener("change", onInteraction, true);
      document.removeEventListener("pointerdown", onInteraction, true); document.removeEventListener("keydown", onInteraction, true);
    };
  }, []);

  const refresh = () => {
    const unsafe = pageUpdateBlocked(); setBlocked(unsafe);
    if (!release || unsafe) return;
    recordManualRefresh(release);
    window.location.reload();
  };
  const later = () => { dismissedRef.current = release; setDismissed(release); };
  return { visible: Boolean(release && release !== dismissed), blocked, refresh, later };
}